import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, FlaskConical } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { BroadcastHistory } from "@/components/admin/BroadcastHistory";

const input = "w-full rounded-xl border border-[#27272A] bg-[#09090B] px-4 py-2.5 text-base text-[#FAF9F6] outline-none focus:border-[#D4AF37]";
const CHANNELS = [["email", "Email"], ["app", "Avviso nell'app"], ["push", "Notifica push"]];
const AUDIENCES = [["all", "Tutti gli iscritti"], ["active", "Con percorso attivo"], ["trial", "In prova (ultimi 7 giorni)"], ["inactive", "Senza percorso attivo"]];
const LINKS = [["", "Nessun pulsante"], ["/oggi", "Pagina Oggi"], ["/percorso", "Percorso"], ["/coach", "Coach Mino"], ["/paywall", "Abbonamento"]];

function Chip({ on, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${on ? "border-[#D4AF37] bg-[#D4AF37]/15 text-[#E5C158]" : "border-[#27272A] text-[#A1A1AA]"}`}>
      {children}
    </button>
  );
}

export default function AdminBroadcasts() {
  const [f, setF] = useState({ title: "", message: "", link: "", kind: "service", channels: ["email", "app"], audience: "all" });
  const [count, setCount] = useState(null);
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(false);
  const [reload, setReload] = useState(0);
  const set = (k, v) => { setF((s) => ({ ...s, [k]: v })); setConfirm(false); };
  const toggleCh = (c) => set("channels", f.channels.includes(c) ? f.channels.filter((x) => x !== c) : [...f.channels, c]);

  useEffect(() => {
    api.post("/admin/broadcasts/count", { audience: f.audience, kind: f.kind }).then(({ data }) => setCount(data)).catch(() => setCount(null));
  }, [f.audience, f.kind]);

  const payload = () => ({ ...f, link: f.link || null });
  const valid = f.title.trim().length >= 2 && f.message.trim().length >= 2 && f.channels.length > 0;

  const run = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "test") {
        const { data } = await api.post("/admin/broadcasts/test", payload());
        toast.success(`Prova inviata a ${data.sent_to}${data.push === false ? " (push non attive sul tuo dispositivo)" : ""}`);
      } else {
        const { data } = await api.post("/admin/broadcasts", payload());
        toast.success(`Messaggio inviato a ${data.recipients} persone`);
        setF((s) => ({ ...s, title: "", message: "" }));
        setConfirm(false);
        setReload((n) => n + 1);
      }
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    } finally {
      setBusy("");
    }
  };

  return (
    <AppShell title="Comunicazioni" greeting="Area admin">
      <div className="space-y-5 px-5 pt-5 pb-12" data-testid="admin-broadcasts">
        <div className="space-y-3 rounded-2xl border border-[#27272A] bg-[#121215] p-5">
          <div className="flex gap-2">
            <Chip on={f.kind === "service"} onClick={() => set("kind", "service")} testid="bc-kind-service">Avviso di servizio</Chip>
            <Chip on={f.kind === "promo"} onClick={() => set("kind", "promo")} testid="bc-kind-promo">Promozione</Chip>
          </div>
          <p className="text-[13px] text-[#8A8E96]">{f.kind === "service" ? "Novità, problemi, account: arriva a tutti." : "Offerte e pubblicità: esclude chi ha detto no, con link per disiscriversi."}</p>
          <input value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="Titolo (es. Novità nel percorso)" maxLength={90} data-testid="bc-title" className={input} />
          <textarea value={f.message} onChange={(e) => set("message", e.target.value)} placeholder="Scrivi il messaggio…" rows={5} maxLength={2000} data-testid="bc-message" className={`${input} resize-none`} />
          <label className="block text-sm text-[#A1A1AA]">Pulsante nel messaggio
            <select value={f.link} onChange={(e) => set("link", e.target.value)} data-testid="bc-link" className={`${input} mt-1`}>
              {LINKS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
        </div>

        <div className="space-y-3 rounded-2xl border border-[#27272A] bg-[#121215] p-5">
          <p className="font-semibold text-[#FAF9F6]">Dove</p>
          <div className="flex flex-wrap gap-2">
            {CHANNELS.map(([c, l]) => <Chip key={c} on={f.channels.includes(c)} onClick={() => toggleCh(c)} testid={`bc-ch-${c}`}>{l}</Chip>)}
          </div>
          <p className="pt-2 font-semibold text-[#FAF9F6]">A chi</p>
          <select value={f.audience} onChange={(e) => set("audience", e.target.value)} data-testid="bc-audience" className={input}>
            {AUDIENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          {count && (
            <p className="text-sm text-[#A1A1AA]" data-testid="bc-count">
              <b className="text-[#FAF9F6]">{count.total}</b> persone{f.channels.includes("push") && <> · <b className="text-[#FAF9F6]">{count.push}</b> con notifiche attive</>}
            </p>
          )}
        </div>

        <div className="space-y-2.5">
          <button onClick={() => run("test")} disabled={!valid || !!busy} data-testid="bc-test" className="btn-outline-gold flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm uppercase tracking-wide">
            {busy === "test" ? <Loader2 size={15} className="animate-spin" /> : <FlaskConical size={15} />} Invia una prova a me
          </button>
          {!confirm ? (
            <button onClick={() => setConfirm(true)} disabled={!valid || !!busy || !count?.total} data-testid="bc-send" className="btn-gold flex w-full items-center justify-center gap-2 rounded-full px-5 py-3 text-sm uppercase tracking-wide">
              <Send size={15} /> Invia a {count?.total ?? "…"} persone
            </button>
          ) : (
            <div className="rounded-2xl border border-[#D4AF37]/50 bg-[#D4AF37]/8 p-4" data-testid="bc-confirm-box">
              <p className="text-sm text-[#FAF9F6]">Confermi l'invio a <b>{count?.total}</b> persone? Non si può annullare.</p>
              <div className="mt-3 flex gap-2">
                <button onClick={() => run("send")} disabled={!!busy} data-testid="bc-confirm" className="btn-gold flex flex-1 items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-semibold">
                  {busy === "send" && <Loader2 size={15} className="animate-spin" />} Sì, invia
                </button>
                <button onClick={() => setConfirm(false)} data-testid="bc-cancel" className="flex-1 rounded-full border border-[#27272A] px-4 py-2.5 text-sm">Annulla</button>
              </div>
            </div>
          )}
        </div>

        <BroadcastHistory reload={reload} />
      </div>
    </AppShell>
  );
}
